// MeldView — renders a single 副露 (called meld) per the standard orientation conventions (see
// meldLayout.ts): the called tile sideways with its position encoding the source, 暗槓 ends face-down,
// 加槓 with the 4th tile stacked sideways. Uses TileView for faces; draws its own face-down back.
//
// P2 front (設計書 §9): the root span carries `fx-pop-in` so a freshly called meld pops on MOUNT
// (theme.css keyframe; CSS animations only replay on mount, so existing melds re-render silently —
// reduced-motion is neutralized by theme.css's [data-motion="reduced"] *[class*="fx-"] rule).
// TileView's 2-layer body change is transparent here: the call signature and the size×1.36 footprint
// the SideCell wrappers reserve are unchanged.
import type { CSSProperties } from 'react';
import type { Meld, Seat, TileId } from '@mj/protocol';
import { TileView } from './TileView';
import { meldLayout } from './meldLayout';

function FaceDownTile({ size }: { size: number }) {
  const h = Math.round(size * 1.36);
  const style: CSSProperties = {
    display: 'inline-block',
    width: size,
    height: h,
    borderRadius: Math.max(2, Math.round(size * 0.11)),
    background: 'linear-gradient(180deg,#1f6b46,#15513a)',
    border: '1px solid #0d3829',
    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.07)',
  };
  return <span style={style} title="裏" />;
}

// A sideways (rotated 90°) tile. rotate() doesn't change the layout box, so wrap it in a box whose
// dimensions are SWAPPED (width = tile height) to reserve the correct footprint. `stack` adds a second
// sideways tile above (加槓).
function SideCell({ tile, size, stack }: { tile: TileId; size: number; stack?: TileId }) {
  const h = Math.round(size * 1.36);
  const rot = (t: TileId) => (
    <span style={{ display: 'inline-flex', transform: 'rotate(90deg)' }}>
      <TileView tile={t} size={size} />
    </span>
  );
  if (stack) {
    return (
      <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', width: h + 2, height: size * 2 + 3, gap: 1 }}>
        {rot(stack)}
        {rot(tile)}
      </span>
    );
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: h + 2, height: size + 2 }}>
      {rot(tile)}
    </span>
  );
}

const KIND_LABEL: Record<string, string> = {
  pon: 'ポン', chii: 'チー', minkan: '大明槓', ankan: '暗槓', shouminkan: '加槓', kita: '北抜き',
};

export function MeldView({ meld, ownerSeat, players, size = 15 }: { meld: Meld; ownerSeat: Seat; players: number; size?: number }) {
  const cells = meldLayout(meld, ownerSeat, players);
  return (
    <span className="fx-pop-in" style={{ display: 'inline-flex', alignItems: 'flex-end', gap: 1 }} title={KIND_LABEL[meld.kind] ?? meld.kind}>
      {cells.map((c, i) =>
        c.faceDown ? (
          <FaceDownTile key={i} size={size} />
        ) : c.rotated ? (
          <SideCell key={i} tile={c.tile} size={size} {...(c.stack ? { stack: c.stack } : {})} />
        ) : (
          <TileView key={i} tile={c.tile} size={size} />
        ),
      )}
    </span>
  );
}
